import { EDatasourceType, ICommonSettings } from "@kuika/kuika-cl-model";
import { PureComponent, ReactNode } from "react";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { CUSTOM_MAIL_PROVIDER_PREFIX } from "../../../../../shared/utility/mail-provider-config";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigSettingSelect, IConfigSettingOption } from "../config-setting-select";
import { ConfigMailSettings, MAIL_SETTINGS_SEARCH_TERMS } from "./app-settings-config-mail";

export interface IConfigMailInputState {
  showModal: boolean;
  editingMailId: string;
  editingMailType?: "smtp" | "aws" | "exchangeServerMail";
}

export interface IConfigMailInputProps {
  isWorkflowMailSetting?: boolean;
  parentConfigValue?: string | null;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleUpdateCustomFormInput?: (formItemName: string[], value: string | null) => void;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
  /** Drop the "Default" (platform/preview) option — used where a real setting MUST be picked (WFE download). */
  hideDefaultOption?: boolean;
  /** When set, the field becomes required and shows this message inline if left empty. */
  requiredMessage?: string;
}

class ConfigMailInput extends PureComponent<IConfigMailInputProps & IWithSDProps, IConfigMailInputState> {
  formInputName = ["apiConfig", "mailSettingsId"];

  constructor(props: IConfigMailInputProps & IWithSDProps) {
    super(props);
    if (props.isWorkflowMailSetting) this.formInputName[1] = "workflowMailSettingsId";

    this.state = {
      showModal: false,
      editingMailId: ""
    };
  }

  openModal = () => {
    this.setState({ showModal: true, editingMailId: "" });
  };

  closeModal = () => {
    this.setState({ showModal: false });
  };

  setEditingMail = (id: string) => {
    // A custom provider is a C# datasource, not a mail-settings record — there is nothing to edit here.
    if (id.startsWith(`${CUSTOM_MAIL_PROVIDER_PREFIX}|`)) return;

    let idParts = id.split("|");
    let editingMailType: string | undefined = idParts[0];
    let editingMailId = idParts[1];
    if (editingMailType !== "smtp" && editingMailType !== "aws" && editingMailType !== "exchangeServerMail")
      editingMailType = undefined;

    this.setState({ editingMailId, editingMailType, showModal: true });
  };

  render() {
    const {
      parentConfigValue,
      commonSettings,
      getSectionDescription,
      handleUpdateCustomFormInput,
      handleCreateCommonConfig,
      handleUpdateCommonConfig,
      handleDeleteCommonConfig
    } = this.props;

    const smtpOptions = commonSettings.smtpSettings?.map((setting) => ({
      ...setting,
      id: `smtp|${setting.id}`
    }));

    const awsOptions = commonSettings.awsSettings?.map((setting) => ({
      ...setting,
      id: `aws|${setting.id}`
    }));

    // The workflow engine's appsettings.json renders only SMTP and AWS mail (RenderWfeAppSettings has no
    // Exchange section), so Exchange Server is not a usable engine mail provider — don't offer it there.
    const exchangeServerMailSettings = this.props.isWorkflowMailSetting
      ? []
      : commonSettings.exchangeServerMailSettings?.map((setting) => ({
          ...setting,
          id: `exchangeServerMail|${setting.id}`
        }));

    // Custom Mail Providers are project datasources rather than mail-settings records. Selecting one hands
    // EVERY mail of the app — the SendMail action as well as password reset, verification and 2FA — to the
    // customer's ICustomMailClient. The workflow engine cannot load customer code, so its own mail provider
    // (isWorkflowMailSetting) is deliberately left with the platform options only.
    const customMailProviderOptions = this.props.isWorkflowMailSetting
      ? []
      : (this.props.sd?.datasources || [])
          .filter((datasource) => datasource.datasourceType === EDatasourceType.CustomMailProvider)
          .map((datasource) => ({
            id: `${CUSTOM_MAIL_PROVIDER_PREFIX}|${datasource.id}`,
            name: `${datasource.name} (Custom Mail Provider)`,
            isCustomMailProvider: true,
            editable: false
          }));

    // Every provider kind is a different setting record, but the picker only needs an id,
    // a name and whether the row can be opened — so they narrow to one shape here.
    const options: IConfigSettingOption[] = [
      ...(this.props.hideDefaultOption ? [] : [{ id: null, name: "Default" }]),
      ...(smtpOptions || []),
      ...(awsOptions || []),
      ...(exchangeServerMailSettings || []),
      ...customMailProviderOptions
    ].map((option): IConfigSettingOption => ({
      id: option.id,
      name: option.name,
      editable: (option as IConfigSettingOption).editable,
      isGlobalCommonConfigOnProjectConfig: (option as IConfigSettingOption).isGlobalCommonConfigOnProjectConfig
    }));
    return (
      <>
        <ConfigMailSettings
          formInputName={this.formInputName}
          showModal={this.state.showModal}
          closeModal={() => {
            this.closeModal();
          }}
          editingMailId={this.state.editingMailId}
          editingMailType={this.state.editingMailType}
          commonSettings={commonSettings}
          getSectionDescription={getSectionDescription}
          handleCreateCommonConfig={handleCreateCommonConfig}
          handleUpdateCommonConfig={handleUpdateCommonConfig}
          handleDeleteCommonConfig={handleDeleteCommonConfig}
        />
        <ConfigSettingSelect
          searchTerms={MAIL_SETTINGS_SEARCH_TERMS}
          label="Provider"
          formInputName={this.formInputName}
          options={options}
          rules={this.props.requiredMessage ? [{ required: true, message: this.props.requiredMessage }] : undefined}
          required={this.props.requiredMessage !== undefined}
          tooltipContentKey={
            this.props.isWorkflowMailSetting ? "appsettings_workflow_mailprovider" : "appsettings_mail_provider"
          }
          onAddNew={this.openModal}
          onEdit={this.setEditingMail}
          parentConfigValue={parentConfigValue}
          onRestore={() => handleUpdateCustomFormInput?.(this.formInputName, parentConfigValue ?? null)}
          dropdownTestId="appSettingsMailProviderDropdown"
          addNewTestId="appSettingsMailProviderAddNewButton"
          editTestId="appSettingsMailProviderEditButton"
        />
      </>
    );
  }
}

const configMailInput = withSD<IConfigMailInputProps>(ConfigMailInput);
export { configMailInput as ConfigMailInput };
